import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { contactServicesList, getStoreDetailsHistory } from "redux/actions";
import { Link, useSearchParams } from "react-router-dom";

import CityDropdownTheme2 from "../CityDropdownTheme2";
import { groupBy } from "lodash";
import "./location-theme2.scss";
import {
  GoogleMap,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { getLocalStorageValue } from "config/helper";
import MapErrorBoundary from "./MapErrorBoundary";
import useGoogleMaps from "hooks/useGoogleMaps";

const LocationTheme2 = ({ storeList }) => {
  const dispatch = useDispatch();
  
  const clientId = getLocalStorageValue("RSAclient-id");
  const userToken = getLocalStorageValue("user-token");
  const memberNumber = getLocalStorageValue("member-number");
  
  const selectedStoreIdFromLS = getLocalStorageValue("selected-store-id");
  const selectedStore = storeList?.find(
    (s) => String(s.ClientStoreId) === String(selectedStoreIdFromLS)
  ) ?? (storeList?.length > 0 ? storeList[0] : {});
  
  const [selectedCity, setSelectedCity] = useState(selectedStore?.ClientStoreName);
  const storeDataCityWise = groupBy(storeList, "StateName");
  const { currentStoreID } = useSelector(({ store }) => store);
  const isUserLoggedIn = useSelector(({ user }) => user.isUserLoggedIn);
  const [storeId, setstoreId] = useState(
    currentStoreID || getLocalStorageValue("selected-store-id")
  );
  const [mapError, setMapError] = useState(false);

  // Initialize selectedCity when storeList becomes available
  useEffect(() => {
    if (storeList?.length > 0 && !selectedCity) {
      const currentStoreId = getLocalStorageValue("selected-store-id");
      const foundStore = storeList.find(
        (s) => String(s.ClientStoreId) === String(currentStoreId)
      );
      const fallbackStore = foundStore || storeList[0];
      
      if (fallbackStore?.ClientStoreName) {
        setSelectedCity(fallbackStore.ClientStoreName);
        setstoreId(fallbackStore.ClientStoreId);
      }
    }
  }, [storeList, selectedCity]);

  useEffect(() => {
    const body = {
      RSAClientId: clientId,
      ClientStoreId: +storeId || 1,
      AppName: isUserLoggedIn ? "shop" : "",
      member_number: isUserLoggedIn ? memberNumber : "",
      user_token: isUserLoggedIn ? userToken : "",
      Version: isUserLoggedIn ? "1" : "",
      DeviceType: isUserLoggedIn ? "web" : "",
    };
    dispatch(contactServicesList(body));
  }, [dispatch, storeId]); // eslint-disable-line

  const { contactserviceData: servicesList } = useSelector(({ feedback }) => feedback)

  const [searchParams] = useSearchParams();
  const storeIdFromUrl = searchParams.get("store");

  useEffect(() => {
    if (storeIdFromUrl && storeList?.length > 0) {
      const store = storeList.find(s => String(s.ClientStoreId) === String(storeIdFromUrl));
      if (store) {
        handleSelectCity(storeIdFromUrl, store.ClientStoreName);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeIdFromUrl, storeList]);

  const handleSelectCity = (store_id, optionName) => {
    setSelectedCity(optionName);
    setstoreId(store_id);
    const body = {
      RSAClientId: clientId,
      ClientStoreId: +store_id,
    };
    dispatch(getStoreDetailsHistory(body));
  };

  const storeSingleData = storeList?.find(
    (storeListData) => storeListData.ClientStoreName === selectedCity
  ) ?? (storeList?.length > 0 ? storeList[0] : null);
  
  const formatPhoneNumber = (str) => {
    let cleaned = ("" + str).replace(/\D/g, "");
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return "(" + match[1] + ") " + match[2] + "-" + match[3];
    }
    return null;
  };
  
  const mapStyles = {
    height: "600px",
    width: "100%",
  };
  
  const info = {
    name: storeSingleData?.ClientStoreName ?? "",
    state: storeSingleData?.StateName ?? "",
    location: {
      lat: parseFloat(storeSingleData?.Latitude) || (storeList?.length > 0 ? parseFloat(storeList[0]?.Latitude) : 0),
      lng: parseFloat(storeSingleData?.Longitude) || (storeList?.length > 0 ? parseFloat(storeList[0]?.Longitude) : 0),
    },
  };

  const [selected, setSelected] = useState({});

  const handleMapLoadError = () => {
    setMapError(true);
  };

  const handleMapLoad = () => {
    setMapError(false);
  };

  function ShowGoogleMap() {
    const { isLoaded, loadError } = useGoogleMaps();
    const googleMapsApiKey = process.env.REACT_APP_REACT_GOOGLE_MAP_KEY;
    
    if (!googleMapsApiKey) {
      return (
        <MapContainer errorMessage="Google Maps API key is not configured!"
         storeImage={storeSingleData?.store_image}
          />
      );
    }

    if (loadError || mapError) {
      return (
        <MapContainer errorMessage="Failed to load Google Maps! Please check your internet connection and try again." 
         storeImage={storeSingleData?.store_image}
         />
      );
    }

    if (!isLoaded) {
      return (
        <MapContainer storeImage={storeSingleData?.store_image}>
          <div className="map-loading">Loading map...</div>
        </MapContainer>
      );
    }

    // Don't render map if coordinates are invalid
    if (!info?.location?.lat || !info?.location?.lng || info?.location?.lat === 0 && info?.location?.lng === 0) {
      return (
        <MapContainer storeImage={storeSingleData?.store_image}>
          <div className="map-error">Store location data is not available</div>
        </MapContainer>
      );
    }

    return (
      <MapContainer storeImage={storeSingleData?.store_image}>
        <MapErrorBoundary>
          <GoogleMap 
            mapContainerStyle={mapStyles} 
            zoom={13} 
            center={info.location}
            onLoad={handleMapLoad}
            onError={handleMapLoadError}
          >
            <Marker 
              position={info.location} 
              onClick={() => setSelected(info)}
            />
            {selected.name && selected.state && (
              <InfoWindow 
                position={selected.location} 
                clickable={true} 
                onCloseClick={() => setSelected({})}
              >
                <p>{selected.name}</p>
              </InfoWindow>
            )}
          </GoogleMap>
        </MapErrorBoundary>
      </MapContainer>
    );
  }

  return (
    <>
      {storeSingleData ? (
        <div className="container">
          <div className="location-dropdown-main-section">
            <div className="location-dropdown-section">
              <div className="location-dropdown-theme2">
                <div className="search-dropdown-list">
                  <label className="search-title">Search Store</label>
                  <div className="tabs">
                    <CityDropdownTheme2
                      lable=""
                      placeholder="Select Store"
                      value={selectedCity}
                      defaultValue=" "
                      onChange={handleSelectCity}
                      optionData={storeDataCityWise}
                    />
                  </div>
                </div>
                <div className="grid store-location-wrapper">
                  <div className="grid-items">
                    <h2>{storeSingleData.City}</h2>
                    <p>{storeSingleData.AddressLine1}</p>
                    {storeSingleData?.AddressLine2 &&
                      <p>
                        {storeSingleData.AddressLine2}
                      </p>
                    }
                    <p>{storeSingleData.City}, {storeSingleData.StateName} - {storeSingleData.ZipCode}</p>
                    <p>
                      <a
                        href={`tel:${storeSingleData.StorePhoneNumber}`}
                        rel="noreferrer"
                      >
                        Phone:{" "}
                        {formatPhoneNumber(storeSingleData.StorePhoneNumber)}
                      </a>
                    </p>
                    {storeSingleData?.fax_number &&
                      <p>FAX Number: {storeSingleData.fax_number}</p>
                    }
                    {storeSingleData?.StoreEmail &&
                      <p>
                        <a
                          href={`mailto:${storeSingleData.StoreEmail}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Email: {storeSingleData.StoreEmail}
                        </a>
                      </p>
                    }
                    <StoreTiming data={storeSingleData} />
                  </div>
                </div>
              </div>
            </div>
            <div className="store-service-section">
              <div className="store-service-header">
                {servicesList.length > 0
                  ? servicesList.map((store) => {
                    return (
                      <>
                        {store.data.length > 0 ? (
                          <>
                            <p>{store.service_section_title}</p>
                            <div className="store-link-wrap">
                              {store.data.map((sub) => {
                                return sub.is_link === "1" ? (
                                  <div className="store-service-link">
                                    {sub.slug ? (
                                      <a
                                        href={`${sub.slug}`}
                                        target="_self"
                                        rel="noreferrer"
                                      >
                                        {sub.name}
                                      </a>
                                    ) : (
                                      <span>{sub.name}</span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="store-service-link">
                                    {sub.slug ? (
                                      <Link
                                        to={`/${sub.slug}`}
                                        target="_self"
                                        rel="noreferrer"
                                      >
                                        {sub.name}
                                      </Link>
                                    ) : (
                                      <span>{sub.name}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ) : (
                          ""
                        )}
                      </>
                    );
                  })
                  : ""}
              </div>
            </div>
          </div>
          <ShowGoogleMap />
          {/* {storeSingleData?.store_image ? (
            <div className="store-location-image">
              <img alt="store_image" src={storeSingleData.store_image} />
            </div>
          ) : null} */}
        </div>
      ) : (
        ""
      )}
    </>
  );
};

function StoreTiming({data}) {
  if (!data) return null

  const timings  = [data?.StoreTimings, data?.StoreTimings2, data?.StoreTimings3].filter(Boolean)

  return (
    <>
      {timings?.map((timing, index) => {
        return <div className="time-text" key={index}>
          <h3>{index === 0 ? "Hours:" : "\u00A0".repeat(11)} {timing}</h3>
        </div>
      })}
    </>
  )
}

const MapContainer = ({ children, errorMessage , storeImage }) => (
  <div className="store-service-location-section">
    <div className="store-service-location-section-map">
      <div className="map-image">
        {errorMessage ? (
          <div className="map-error">
            <div>{errorMessage}</div>
          </div>
        ) : (
          children
        )}
      </div>
      {storeImage && (
          <div className="store-location-image">
            <img alt="store_image" src={storeImage} />
          </div>
        )}
    </div>
  </div>
);

export default LocationTheme2;
